import React, { Component } from 'react';
import { RichLinkCard } from './RichLinkCard';
import { MetricBadge } from './MetricBadge';
import { CodeBlock } from './CodeBlock';
import { ImageGallery } from './ImageGallery';
import { EnhancedTable } from './EnhancedTable';
import { ChecklistRenderer } from './ChecklistRenderer';
import { MarkdownRenderer } from '../MarkdownRenderer';
interface ContentBlock {
  type: 'text' | 'link' | 'metric' | 'code' | 'image' | 'table' | 'checklist';
  content: string;
  metadata?: Record<string, any>;
}
interface AdaptiveContentRendererProps {
  content: string;
  isUser?: boolean;
}
export function AdaptiveContentRenderer({
  content,
  isUser
}: AdaptiveContentRendererProps) {
  // Don't parse user messages - render as plain text
  if (isUser) {
    return <p className="text-sm whitespace-pre-wrap leading-relaxed" data-id="element-283">{content}</p>;
  }
  const blocks = parseContent(content);
  return <div className="space-y-3" data-id="element-284">
      {blocks.map((block, index) => <ContentBlock key={index} block={block} data-id="element-285" />)}
    </div>;
}
function ContentBlock({
  block
}: {
  block: ContentBlock;
}) {
  switch (block.type) {
    case 'link':
      return <RichLinkCard url={block.content} metadata={block.metadata} data-id="element-286" />;
    case 'metric':
      return <MetricBadge content={block.content} metadata={block.metadata} data-id="element-287" />;
    case 'code':
      return <CodeBlock code={block.content} language={block.metadata?.language} data-id="element-288" />;
    case 'image':
      return <ImageGallery images={block.metadata?.images || [block.content]} data-id="element-289" />;
    case 'table':
      return <EnhancedTable content={block.content} data-id="element-290" />;
    case 'checklist':
      return <ChecklistRenderer content={block.content} data-id="element-291" />;
    case 'text':
    default:
      return <MarkdownRenderer content={block.content} data-id="element-292" />;
  }
}
function parseContent(content: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  const lines = content.split('\n');
  let currentBlock = '';
  let currentType: ContentBlock['type'] = 'text';
  let inCodeBlock = false;
  let codeLanguage = '';
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Detect code blocks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        // End of code block
        blocks.push({
          type: 'code',
          content: currentBlock.trim(),
          metadata: {
            language: codeLanguage
          }
        });
        currentBlock = '';
        inCodeBlock = false;
        codeLanguage = '';
      } else {
        // Start of code block
        if (currentBlock.trim()) {
          blocks.push({
            type: currentType,
            content: currentBlock.trim()
          });
          currentBlock = '';
        }
        inCodeBlock = true;
        codeLanguage = line.trim().slice(3).trim();
      }
      continue;
    }
    if (inCodeBlock) {
      currentBlock += line + '\n';
      continue;
    }
    // Detect URLs
    const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
    if (urlMatch && line.trim() === urlMatch[0]) {
      if (currentBlock.trim()) {
        blocks.push({
          type: currentType,
          content: currentBlock.trim()
        });
        currentBlock = '';
      }
      blocks.push({
        type: 'link',
        content: urlMatch[0],
        metadata: {
          title: extractTitleFromUrl(urlMatch[0])
        }
      });
      continue;
    }
    // Detect metrics (e.g., "Margin: 18.2%", "Active Jobs: 7")
    const metricMatch = line.match(/^([A-Za-z\s]+):\s*([\d.]+%?|[\d,]+)$/);
    if (metricMatch) {
      if (currentBlock.trim()) {
        blocks.push({
          type: currentType,
          content: currentBlock.trim()
        });
        currentBlock = '';
      }
      blocks.push({
        type: 'metric',
        content: line,
        metadata: {
          label: metricMatch[1],
          value: metricMatch[2]
        }
      });
      continue;
    }
    // Detect images
    const imageMatch = line.match(/!\[.*?\]\((https?:\/\/[^\s)]+)\)/);
    if (imageMatch) {
      if (currentBlock.trim()) {
        blocks.push({
          type: currentType,
          content: currentBlock.trim()
        });
        currentBlock = '';
      }
      blocks.push({
        type: 'image',
        content: imageMatch[1],
        metadata: {
          images: [imageMatch[1]]
        }
      });
      continue;
    }
    // Detect checklists (lines starting with - [ ] or - [x])
    if (line.match(/^[\s]*-\s\[([ x])\]/i)) {
      if (currentType !== 'checklist' && currentBlock.trim()) {
        blocks.push({
          type: currentType,
          content: currentBlock.trim()
        });
        currentBlock = '';
        currentType = 'checklist';
      }
      currentBlock += line + '\n';
      currentType = 'checklist';
      continue;
    } else if (currentType === 'checklist' && line.trim() === '') {
      blocks.push({
        type: 'checklist',
        content: currentBlock.trim()
      });
      currentBlock = '';
      currentType = 'text';
      continue;
    }
    // Detect tables (lines with | separators)
    if (line.includes('|') && line.split('|').length > 2) {
      if (currentType !== 'table' && currentBlock.trim()) {
        blocks.push({
          type: currentType,
          content: currentBlock.trim()
        });
        currentBlock = '';
        currentType = 'table';
      }
      currentBlock += line + '\n';
      currentType = 'table';
      continue;
    } else if (currentType === 'table' && !line.includes('|')) {
      blocks.push({
        type: 'table',
        content: currentBlock.trim()
      });
      currentBlock = '';
      currentType = 'text';
    }
    // Regular text
    if (currentType !== 'text' && !line.trim()) {
      if (currentBlock.trim()) {
        blocks.push({
          type: currentType,
          content: currentBlock.trim()
        });
      }
      currentBlock = '';
      currentType = 'text';
      continue;
    }
    currentBlock += line + '\n';
  }
  // Push remaining content
  if (currentBlock.trim()) {
    blocks.push({
      type: currentType,
      content: currentBlock.trim()
    });
  }
  return blocks;
}
function extractTitleFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname.split('/').filter(Boolean).pop();
    return path ? decodeURIComponent(path) : urlObj.hostname;
  } catch {
    return url;
  }
}